import type { Connection, Transaction } from '@solana/web3.js';
import type { WalletContextState } from '@solana/wallet-adapter-react';

/**
 * Sign a transaction via the wallet adapter, then send the raw bytes through
 * our own RPC connection (Helius) instead of the wallet's built-in RPC.
 *
 * Phantom's `sendTransaction` / `signAndSendTransaction` routes simulation
 * and submission through Phantom's own RPC, which may not be able to
 * simulate accounts that only exist on our dedicated RPC. This causes a
 * misleading "User rejected the request" error.
 *
 * By using `signTransaction` + `connection.sendRawTransaction` we bypass
 * Phantom's RPC entirely and send through our Helius endpoint.
 */
export async function sendViaRpc(
  transaction: Transaction,
  connection: Connection,
  wallet: Pick<WalletContextState, 'signTransaction'>,
): Promise<string> {
  if (!wallet.signTransaction) {
    throw new Error('Wallet does not support signTransaction');
  }

  const signed = await wallet.signTransaction(transaction);
  const signature = await connection.sendRawTransaction(signed.serialize(), {
    skipPreflight: true,
  });
  return signature;
}
